import { zodResolver } from '@hookform/resolvers/zod';
import type { Dayjs } from 'dayjs';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Drawer } from '@/components/feedback/drawer';
import { DateField, MultiSelectField, NumberField } from '@/components/forms';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';

import { toDefaults, toFilters } from '../filters';
import { ORDER_STATUS_CHIP } from '../format';
import { useOrderCities, useOrderCustomers } from '../hooks';
import { orderFiltersSchema } from '../schemas';
import type { OrderFilterFormValues, OrderListFilters } from '../types';

const STATUS_OPTIONS = (Object.keys(ORDER_STATUS_CHIP) as (keyof typeof ORDER_STATUS_CHIP)[]).map(
  (value) => ({ value, label: ORDER_STATUS_CHIP[value].label }),
);

const disableFuture = (d: Dayjs) => d.isAfter(new Date(), 'day');

export type OrderFilterDrawerProps = {
  open: boolean;
  /** Last applied filters; the draft re-seeds from this on each open. */
  value: OrderListFilters;
  onApply: (filters: OrderListFilters) => void;
  onClose: () => void;
};

type BodyProps = Omit<OrderFilterDrawerProps, 'open'>;

function FilterBody({ value, onApply, onClose }: BodyProps) {
  const draft = toDefaults(value);
  const form = useForm<OrderFilterFormValues>({
    resolver: zodResolver(orderFiltersSchema),
    defaultValues: {
      ...draft,
      orderedFrom: draft.orderedFrom ?? null,
      orderedTo: draft.orderedTo ?? null,
    },
    mode: 'onBlur',
  });

  const cities = useOrderCities();
  const cityOptions = useMemo(
    () => (cities.data ?? []).map((c) => ({ value: c, label: c })),
    [cities.data],
  );

  const [customerSearch, setCustomerSearch] = useState('');
  const onSearch = useDebouncedCallback((term: string) => setCustomerSearch(term), 300);
  const customers = useOrderCustomers(customerSearch);
  const customerOptions = useMemo(
    () => (customers.data ?? []).map((c) => ({ value: c.id, label: c.name })),
    [customers.data],
  );

  const handleApply = form.handleSubmit((values) => {
    onApply(toFilters(values));
    onClose();
  });

  return (
    <Drawer
      open
      onClose={onClose}
      title="Filter"
      width={400}
      primaryText="Apply"
      onPrimary={handleApply}
      cancelText="Cancel"
      onCancel={onClose}
    >
      <MultiSelectField
        name="customerIds"
        control={form.control}
        label="Customers"
        placeholder="Select"
        showSearch={{ onSearch, filterOption: false }}
        loading={customers.isFetching}
        options={customerOptions}
      />

      <MultiSelectField
        name="cities"
        control={form.control}
        label="City"
        placeholder="Select"
        options={cityOptions}
      />

      <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
        <legend>Order Date Range</legend>
        <div style={{ display: 'flex', gap: 12 }}>
          <DateField
            name="orderedFrom"
            control={form.control}
            aria-label="Order Date From"
            placeholder="mm/dd/yy"
            disabledDate={disableFuture}
            style={{ width: '100%' }}
          />
          <DateField
            name="orderedTo"
            control={form.control}
            aria-label="Order Date To"
            placeholder="mm/dd/yy"
            disabledDate={disableFuture}
            style={{ width: '100%' }}
          />
        </div>
      </fieldset>

      <MultiSelectField
        name="statuses"
        control={form.control}
        label="Order Status"
        placeholder="Select"
        options={STATUS_OPTIONS}
      />

      <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
        <legend>Order Amount ($)</legend>
        <div style={{ display: 'flex', gap: 12 }}>
          <NumberField
            name="amountFrom"
            control={form.control}
            aria-label="Order Amount From"
            placeholder="From"
            min={0}
            precision={2}
            style={{ width: '100%' }}
          />
          <NumberField
            name="amountTo"
            control={form.control}
            aria-label="Order Amount To"
            placeholder="To"
            min={0}
            precision={2}
            style={{ width: '100%' }}
          />
        </div>
      </fieldset>

      <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
        <legend>No of Devices</legend>
        <div style={{ display: 'flex', gap: 12 }}>
          <NumberField
            name="deviceCountFrom"
            control={form.control}
            aria-label="No of Devices From"
            placeholder="From"
            min={1}
            precision={0}
            style={{ width: '100%' }}
          />
          <NumberField
            name="deviceCountTo"
            control={form.control}
            aria-label="No of Devices To"
            placeholder="To"
            min={1}
            precision={0}
            style={{ width: '100%' }}
          />
        </div>
      </fieldset>
    </Drawer>
  );
}

export function OrderFilterDrawer({ open, ...rest }: OrderFilterDrawerProps) {
  if (!open) {
    return (
      <Drawer open={false} onClose={rest.onClose}>
        {null}
      </Drawer>
    );
  }
  // Unmount on close; remounts fresh on open so the draft re-seeds from the last applied `value`.
  return <FilterBody {...rest} />;
}
